// src/features/records/hooks/useSharedRecordCounts.js

import { useState, useEffect, useRef, useMemo } from 'react';
import { getSharedRecordCounts } from '../services/edges/counts';

/**
 * Hook to fetch and cache shared record counts for multiple users
 * 
 * Current Implementation:
 * - Uses getCountFromServer() for O(1) aggregation queries
 * - 5-minute TTL cache balances freshness vs. performance
 * - Sequence guard prevents stale data from overwriting fresh data
 * 
 * Future Optimization Path:
 * When we have denormalized counters (see legacy service notes),
 * this hook can be simplified to just read pre-computed values,
 * potentially with real-time listeners for instant updates.
 * 
 * @param {Array<string>} userIds - Array of user IDs to fetch counts for
 * @param {Object} options - Options object
 * @param {boolean} options.enabled - Whether to fetch counts (default: true)
 * @param {number} options.ttl - Cache TTL in milliseconds (default: 5 minutes)
 * @returns {Object} { counts: Map<string, number>, loading: boolean, error: Error|null }
 */
function useSharedRecordCounts(userIds, options = {}) {
  const { enabled = true, ttl = 5 * 60 * 1000 } = options;
  
  const [counts, setCounts] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  
  // Cache with TTL
  const cacheRef = useRef(new Map()); // Map<userIdsKey, { data: Map, timestamp: number }>
  const sequenceRef = useRef(0); // Sequence guard instead of abort controller
  
  // Create a stable key from userIds for caching
  // Use useMemo to ensure this only changes when userIds content changes
  const userIdsKey = useMemo(() => {
    if (!userIds || userIds.length === 0) return '';
    return [...userIds].sort().join(',');
  }, [userIds]);
  
  useEffect(() => {
    if (!enabled || !userIds || userIds.length === 0) {
      setCounts(new Map());
      return;
    }
    
    // Check cache first
    const cached = cacheRef.current.get(userIdsKey);
    if (cached && Date.now() - cached.timestamp < ttl) {
      setCounts(cached.data);
      return;
    }
    
    // Increment sequence for this request
    const currentSequence = ++sequenceRef.current;
    
    const fetchCounts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const countsMap = await getSharedRecordCounts(userIds);
        
        // Check if this is still the current request
        if (currentSequence !== sequenceRef.current) {
          return; // Stale request, ignore results
        }
        
        // Update cache
        cacheRef.current.set(userIdsKey, {
          data: countsMap,
          timestamp: Date.now()
        });
        
        setCounts(countsMap);
      } catch (err) {
        // Only handle error if this is still the current request
        if (currentSequence !== sequenceRef.current) {
          return;
        }
        
        console.error('Error fetching shared record counts:', err);
        setError(err);
        // Set empty counts on error
        const emptyCounts = new Map();
        userIds.forEach(id => emptyCounts.set(id, 0));
        setCounts(emptyCounts);
      } finally {
        // Only update loading state if this is still the current request
        if (currentSequence === sequenceRef.current) {
          setLoading(false);
        }
      }
    };
    
    fetchCounts();
  }, [userIdsKey, userIds, enabled, ttl, refreshToken]);
  
  // Helper function to get count for a specific user
  const getCountForUser = (userId) => {
    return counts.get(userId) || 0;
  };
  
  // Helper function to refresh counts
  const refresh = () => {
    // Clear cache for this key
    cacheRef.current.delete(userIdsKey);
    // Increment refresh token to trigger re-fetch
    setRefreshToken(prev => prev + 1);
  };
  
  return {
    counts,
    getCountForUser,
    loading,
    error,
    refresh
  };
}

export default useSharedRecordCounts;
